import { IFieldDefFn } from "@components/smart/FieldInfo";
import { getCommonFilterDefs } from "@components/smart/GeneralFieldDefinition";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    BankAccountEntity,
    EntitySetName,
    EntityTypeName,
    IPrRetirementSavingsProductEntity,
    PrRetirementSavingsCompanyEntity,
    PrRetirementSavingsProductEntity
} from "@odata/GeneratedEntityTypes";
import { IFormatOptions } from "@odata/OData.utils";
import { getBankAccountsFieldBaseDef } from "@pages/banks/bankAccounts/BankAccounts.utils";
import { getBusinessPartnerFieldDefs } from "@pages/businessPartner/BusinessPartner.utils";
import RetirementSavingsCompanyFormView from "@pages/payroll/retirementSavingsCompany/RetirementSavingsCompanyFormView";
import { SavingsTypeForm } from "@pages/payroll/retirementSavingsCompany/SavingsTypeForm";
import { forEachKey } from "@utils/general";
import i18next from "i18next";
import React from "react";

import { BasicInputSizes, FieldType, Sort, TextAlign } from "../../../enums";
import { TValue } from "../../../global.types";
import { Model } from "../../../model/Model";
import { IFormDef } from "../../../views/formView/Form";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../../PageUtils";


const INSTITUTIONS_PATH = BindingContext.localContext("INSTITUTIONS");

export const getDefinitions: IGetDefinition = (): IDefinition => {
    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.PrRetirementSavingsCompany}Table`,
        title: i18next.t("RetirementSavingsCompany:Table.Title"),
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            defaultFilters: [
                PrRetirementSavingsCompanyEntity.Name,
                PrRetirementSavingsCompanyEntity.DataBoxId
            ],
            filterDefinition: {
                ...getCommonFilterDefs(),
                [PrRetirementSavingsCompanyEntity.Name]: {},
                [PrRetirementSavingsCompanyEntity.DataBoxId]: {}
            },
            isValueHelp: true
        }],
        initialSortBy: [{ id: PrRetirementSavingsCompanyEntity.Name, sort: Sort.Desc }],
        columns: [
            PrRetirementSavingsCompanyEntity.Name,
            PrRetirementSavingsCompanyEntity.Products
        ],
        columnDefinition: {
            [PrRetirementSavingsCompanyEntity.Products]: {
                additionalProperties: [{ id: PrRetirementSavingsProductEntity.Type }],
                textAlign: TextAlign.Left,
                formatter: (val: TValue, args?: IFormatOptions) => {
                    return args.item?.Products?.map((item: IPrRetirementSavingsProductEntity) => {
                        return item.Type?.Name;
                    }).join(", ");
                }
            },
            [PrRetirementSavingsCompanyEntity.Name]: {},
            [PrRetirementSavingsCompanyEntity.DataBoxId]: {}
        },
    };

    const summary: ISummaryItem[] = [
        { id: PrRetirementSavingsCompanyEntity.Name }
    ];

    const form: IFormDef = {
        id: `${EntityTypeName.PrRetirementSavingsCompany}Form`,
        translationFiles: getDefinitions.translationFiles,
        isDeletable: true,
        getItemBreadCrumbText: (storage: Model) =>
            getItemBreadCrumbsText(storage, i18next.t("RetirementSavingsCompany:Form.Title"), storage.data.entity?.Name),
        title: i18next.t("RetirementSavingsCompany:Form.Title"),
        formControl: RetirementSavingsCompanyFormView,
        summary,
        additionalProperties: [
            { id: createPath(PrRetirementSavingsCompanyEntity.Products, PrRetirementSavingsProductEntity.Type) },
            { id: createPath(PrRetirementSavingsCompanyEntity.Products, PrRetirementSavingsProductEntity.BankAccount, BankAccountEntity.AccountNumber) },
            { id: createPath(PrRetirementSavingsCompanyEntity.Products, PrRetirementSavingsProductEntity.BankAccount, BankAccountEntity.Bank) },
            { id: createPath(PrRetirementSavingsCompanyEntity.Products, PrRetirementSavingsProductEntity.BankAccount, BankAccountEntity.BankCode) },
            { id: createPath(PrRetirementSavingsCompanyEntity.Products, PrRetirementSavingsProductEntity.BankAccount, BankAccountEntity.AbaNumber) },
            { id: createPath(PrRetirementSavingsCompanyEntity.Products, PrRetirementSavingsProductEntity.BankAccount, BankAccountEntity.IBAN) },
            { id: createPath(PrRetirementSavingsCompanyEntity.Products, PrRetirementSavingsProductEntity.BankAccount, BankAccountEntity.SWIFT) },
            { id: createPath(PrRetirementSavingsCompanyEntity.Products, PrRetirementSavingsProductEntity.BankAccount, BankAccountEntity.Country) }
        ],
        fieldDefinition: {
            [PrRetirementSavingsCompanyEntity.Name]: {
                width: BasicInputSizes.XL
            },
            [PrRetirementSavingsCompanyEntity.DataBoxId]: {},
            [INSTITUTIONS_PATH]: {
                type: FieldType.Custom,
                useForValidation: false,
                customizationData: {
                    useForCustomization: false
                },
                render: (args: IFieldDefFn) => {
                    return <SavingsTypeForm
                        onChange={args.events.onChange}
                        onBlur={args.events.onBlur}
                        storage={args.storage as FormStorage}/>;
                }
            },
            ...getBusinessPartnerFieldDefs({
                navigationPath: "",
                nameProp: PrRetirementSavingsCompanyEntity.LegalName
            })
        },
        groups: [
            {
                id: "main",
                rows: [[
                    { id: PrRetirementSavingsCompanyEntity.Name },
                    { id: PrRetirementSavingsCompanyEntity.DataBoxId },
                ]]
            },
            {
                id: "counterparty",
                title: i18next.t("RetirementSavingsCompany:Form.Counterparty"),
                rows: [[
                    { id: PrRetirementSavingsCompanyEntity.LegalName },
                    { id: PrRetirementSavingsCompanyEntity.LegalNumber },
                    { id: PrRetirementSavingsCompanyEntity.TaxNumber },
                ]]
            },
            {
                id: "institutions",
                rows: [[{ id: INSTITUTIONS_PATH }]]

            }
        ]
    };

    form.fieldDefinition[PrRetirementSavingsCompanyEntity.LegalName] = {
        ...form.fieldDefinition[PrRetirementSavingsCompanyEntity.LegalName],
        label: i18next.t("RetirementSavingsCompany:Form.Counterparty")
    };

    const path = `${PrRetirementSavingsCompanyEntity.Products}/${PrRetirementSavingsProductEntity.BankAccount}`;
    const BaseBankFieldDefs = getBankAccountsFieldBaseDef();
    forEachKey(BaseBankFieldDefs, (key) => {
        form.fieldDefinition[`${path}/${key}`] = {
            ...BaseBankFieldDefs[key],
            useForValidation: true
        };
    });

    const def = {
        entitySet: EntitySetName.PrRetirementSavingsCompanies,
        table,
        form
    };

    return def;
};

getDefinitions.translationFiles = ["RetirementSavingsCompany", "Common", "Employee"];
setDefByEntityType(EntityTypeName.PrRetirementSavingsCompany, getDefinitions);